/**
 * Карта v3 — экспорт.
 *
 * SVG собирается из той же сцены, что и экран, поэтому печатная версия
 * совпадает с тем, что видно. Ни html2canvas, ни клонирования DOM —
 * обе прошлые карты спотыкались именно на этом.
 */
import { THEME, DASH, CONT_R, NODE_R, PAD_TOP } from './const';
import { sceneBounds } from './scene';

const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

function pathD(pts, r = 7) {
  if (pts.length < 4) return '';
  let d = `M ${pts[0].toFixed(1)} ${pts[1].toFixed(1)}`;
  for (let i = 2; i + 1 < pts.length; i += 2) {
    const x = pts[i], y = pts[i + 1];
    if (i + 3 >= pts.length) { d += ` L ${x.toFixed(1)} ${y.toFixed(1)}`; break; }
    const px = pts[i - 2], py = pts[i - 1];
    const nx = pts[i + 2], ny = pts[i + 3];
    const d1 = Math.hypot(x - px, y - py) || 1;
    const d2 = Math.hypot(nx - x, ny - y) || 1;
    const rr = Math.min(r, d1 / 2, d2 / 2);
    const t1x = x - ((x - px) / d1) * rr, t1y = y - ((y - py) / d1) * rr;
    const t2x = x + ((nx - x) / d2) * rr, t2y = y + ((ny - y) / d2) * rr;
    d += ` L ${t1x.toFixed(1)} ${t1y.toFixed(1)} Q ${x.toFixed(1)} ${y.toFixed(1)} ${t2x.toFixed(1)} ${t2y.toFixed(1)}`;
  }
  return d;
}

export function toSVG(scene, ui = {}) {
  const T = THEME[ui.theme === 'dark' ? 'dark' : 'light'];
  const b = sceneBounds(scene);
  const pad = 60;
  const legendH = 34;
  const W = Math.ceil(b.w + pad * 2);
  const H = Math.ceil(b.h + pad * 2 + legendH);
  const ox = pad - b.x, oy = pad - b.y;

  const out = [];
  out.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">`);
  out.push(`<rect width="${W}" height="${H}" fill="${T.bg}"/>`);
  out.push(`<g transform="translate(${ox},${oy})" font-family="system-ui,-apple-system,Segoe UI,Roboto,sans-serif">`);

  for (const id of scene.order) {
    const c = scene.conts.get(id);
    if (!c) continue;
    out.push(
      `<rect x="${c.x}" y="${c.y}" width="${c.w}" height="${c.h}" rx="${CONT_R}" ` +
      `fill="${c.fill}" fill-opacity="${(c.opacity || 18) / 100}" stroke="${c.border}" stroke-width="1.4"/>`,
      `<text x="${c.x + 14}" y="${c.y + PAD_TOP / 2 + 5}" font-size="14" font-weight="600" fill="${T.contText}">${esc(c.name)}</text>`,
    );
  }

  for (const e of scene.edges.values()) {
    if (!e.route) continue;
    const dash = (DASH[e.style] || []).join(' ');
    out.push(
      `<path d="${pathD(e.route.pts)}" fill="none" stroke="${e.color}" stroke-width="${e.width}" ` +
      `stroke-linecap="round" stroke-linejoin="round"${dash ? ` stroke-dasharray="${dash}"` : ''}/>`,
    );
  }

  if (ui.labels && ui.labels !== 'none') {
    for (const e of scene.edges.values()) {
      if (!e.route || e.labelCfg?.hidden) continue;
      const text = ui.labels === 'iface'
        ? [e.aIface, e.bIface].filter(Boolean).join(' → ')
        : ui.labels === 'subnet' ? (e.subnet || e.vlan || '') : (e.label || '');
      if (!text) continue;
      const pts = e.route.pts;
      const mi = Math.floor(pts.length / 4) * 2;
      const x = (pts[mi] + pts[mi + 2]) / 2, y = (pts[mi + 1] + pts[mi + 3]) / 2;
      const w = text.length * 6 + 10;
      out.push(
        `<rect x="${(x - w / 2).toFixed(1)}" y="${(y - 9).toFixed(1)}" width="${w}" height="18" rx="5" ` +
        `fill="${T.labelBg}" stroke="${T.labelBorder}"/>`,
        `<text x="${x.toFixed(1)}" y="${(y + 4).toFixed(1)}" font-size="11" text-anchor="middle" fill="${T.labelText}">${esc(text)}</text>`,
      );
    }
  }

  for (const n of scene.nodes.values()) {
    if (n.hidden) continue;
    out.push(
      `<rect x="${n.x}" y="${n.y}" width="${n.w}" height="${n.h}" rx="${NODE_R}" ` +
      `fill="${T.node}" stroke="${T.nodeBorder}" stroke-width="1.2"/>`,
      `<text x="${n.x + 36}" y="${n.y + n.h / 2 + 3}" font-size="12" font-weight="600" fill="${T.nodeText}">${esc(n.name)}</text>`,
      `<text x="${n.x + 36}" y="${n.y + n.h / 2 + 17}" font-size="10" fill="${T.nodeSub}">${esc(n.ip || n.typeName || '')}</text>`,
      `<circle cx="${n.x + 20}" cy="${n.y + n.h / 2 - 2}" r="8" fill="none" stroke="${T.sel}" stroke-width="1.4"/>`,
    );
  }
  out.push('</g>');

  // легенда типов связей — чертёж без легенды не читается
  let lx = pad;
  const ly = H - legendH / 2;
  const seen = new Set();
  for (const e of scene.edges.values()) {
    const key = `${e.color}|${e.style}|${e.label}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const dash = (DASH[e.style] || []).join(' ');
    out.push(
      `<line x1="${lx}" y1="${ly}" x2="${lx + 26}" y2="${ly}" stroke="${e.color}" stroke-width="${e.width}"${dash ? ` stroke-dasharray="${dash}"` : ''}/>`,
      `<text x="${lx + 32}" y="${ly + 4}" font-size="11" fill="${T.labelText}" font-family="system-ui,sans-serif">${esc(e.label || '')}</text>`,
    );
    lx += 42 + (e.label || '').length * 6.2;
  }

  out.push('</svg>');
  return out.join('\n');
}

export function download(name, content, mime) {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export function exportSVG(scene, ui) {
  download('map-v3.svg', toSVG(scene, ui), 'image/svg+xml;charset=utf-8');
}

/** PNG — через отрисовку SVG в canvas, без внешних библиотек. */
export function exportPNG(scene, ui, scale = 2) {
  const svg = toSVG(scene, ui);
  const b = sceneBounds(scene);
  const W = Math.ceil((b.w + 120) * scale);
  const H = Math.ceil((b.h + 154) * scale);
  const img = new Image();
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  return new Promise((resolve, reject) => {
    img.onload = () => {
      const cv = document.createElement('canvas');
      cv.width = W; cv.height = H;
      const c = cv.getContext('2d');
      c.fillStyle = ui.theme === 'dark' ? '#0f141b' : '#ffffff';
      c.fillRect(0, 0, W, H);
      c.drawImage(img, 0, 0, W, H);
      cv.toBlob((bl) => {
        URL.revokeObjectURL(url);
        if (bl) { download('map-v3.png', bl, 'image/png'); resolve(true); }
        else reject(new Error('Не удалось собрать PNG'));
      }, 'image/png');
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Не удалось отрисовать SVG')); };
    img.src = url;
  });
}

/** Журнал кабелей: что куда идёт — для монтажников. */
export function exportCablesCSV(scene) {
  const rows = [['Связь', 'Тип', 'От устройства', 'Порт', 'К устройству', 'Порт', 'Подсеть', 'VLAN', 'Изгибов']];
  for (const e of scene.edges.values()) {
    const a = scene.nodes.get(e.a), b = scene.nodes.get(e.b);
    rows.push([
      e.connId, e.label || '', a?.name || '', e.aIface || '',
      b?.name || '', e.bIface || '', e.subnet || '', e.vlan || '', (e.vias || []).length,
    ]);
  }
  const csv = '﻿' + rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\r\n');
  download('cables.csv', csv, 'text/csv;charset=utf-8');
}
